import { Dialog, Popover, Transition } from "@headlessui/react"
import { ChevronRightIcon, XMarkIcon } from "@heroicons/react/24/outline"
import {
	Accordion,
	Box,
	Button,
	CloseButton,
	Group,
	Loader,
	LoadingOverlay,
	Modal,
	Overlay,
	Radio,
	SimpleGrid,
	Tabs
} from "@mantine/core"
import { useInterval } from "@mantine/hooks"
import React, { useEffect, useState } from "react"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { useContentContext } from "../utils/ContentProvider"
import { classNames, cleanString, getCommonName } from "../utils/helpers"
import {
	companyHasDeal,
	getCompanyDescription,
	getLevelOptions,
	getProductName,
	getResultCountForLevelOfCover,
	getVisibleValue,
	placeholderResults
} from "../utils/options";
import { doMatrixQuery, doQuotesQuery, sendEvent } from "../utils/queries"
import { getRuntimeHostname, getRuntimeReferrer, isVertaaensinHost } from "../utils/runtimeOverrides"
import {
	Coverage,
	FilterType,
	OptionItem,
	ResultRow,
	resultTabs,
	toCapitalFirstLetter,
} from "../utils/types"
import ResultFilters from "./ResultFilters"
import SingleResultRow from "./SingleResultRow"

const usePlaceholderResults: boolean = process.env.NODE_ENV === "production" ? false : false
const isProd: boolean =
	(document.baseURI.indexOf("wl.vakuutuslaskuri") > -1);

const Results = ({ company }: any) => {
	const params = useParams()
	const location = useLocation()
	const navigate = useNavigate()
	const referrer = getRuntimeReferrer()
	const referrerLower = referrer.toLowerCase()
	const hostname = getRuntimeHostname()
	const referrerIsVakuutuslaskuri: boolean = (referrerLower.indexOf("vakuutustenvertailu.fi") > -1) ||
		(referrerLower.indexOf("vakuutusvertailu.fi") > -1)
	const referrerIsEffortia: boolean = (referrerLower.indexOf("vakuutustenvertailu") > -1) ||
		(referrerLower.indexOf("vakuutusvertailu") > -1) ||
		(referrerLower.indexOf("effortia") > -1) ||
		(hostname === "testisivusto.vakuutustenvertailu.fi")
	const referrerIsVertaaensin: boolean = (referrerLower.indexOf("vertaaensin") > -1)
	const showVakuutustenvertailuLogo: boolean = (referrerLower.indexOf("vakuutustenvertailu") > -1) ||
		(referrerLower.indexOf("vakuutusvertailu") > -1) ||
		(referrerLower.indexOf("effortia") > -1)
	const isVakuutustenvertailuHost: boolean = (hostname.indexOf("vakuutustenvertailu") > -1) ||
		(hostname.indexOf("vakuutusvertailu") > -1) ||
		(hostname.indexOf("effortia") > -1) ||
		(referrerLower.indexOf("vakuutustenvertailu") > -1) ||
		(referrerLower.indexOf("vakuutusvertailu") > -1) ||
		(referrerLower.indexOf("effortia") > -1)
	const isVertaaensinLogoHost: boolean = (hostname.indexOf("vertaaensin") > -1) ||
		(referrerLower.indexOf("vertaaensin") > -1)
	const logoSrc = isVertaaensinLogoHost
		? "/vertaaensin-logo-white.svg"
		: (isVakuutustenvertailuHost ? "/Vakuutustenvertailu-logo-white.png" : "/Vakuutuslaskuri-logo-white.png")
	const logoAlt = isVertaaensinLogoHost
		? "Vertaaensin logo"
		: (isVakuutustenvertailuHost ? "Vakuutustenvertailu logo" : "Vakuutuslaskuri logo")
	const isVertaaensinTheme = isVertaaensinHost() || (referrerLower.indexOf("vertaaensin") > -1)
	const primaryCtaClass = isVertaaensinTheme
		? "bg-[#f58423] hover:bg-[#f58423] text-white"
		: "bg-[#0F6CBD] hover:bg-[#0c589a] text-white"
	const insuranceHeaderBrandClass = isVertaaensinTheme
		? "vertaaensin"
		: (referrerIsEffortia ? "vakuutustenvertailu" : "")
	const { translations, lang } = useContentContext()
	const [isLoading, setIsLoading] = useState(true)
	const [results, setResults] = useState<ResultRow[]>([])
	const [originalResults, setOriginalResults] = useState<ResultRow[]>([])
	const [levelOfCover, setLevelOfCover] = useState<string>("kaikki")
	const [optional, setOptional] = useState<Array<string>>([])
	const [commonNames, setCommonNames] = useState<string[]>([])
	const [rating, setRating] = useState<string>("")
	const [partners, setPartners] = useState<Array<string>>([])
	const [filterOptions, setFilterOptions] = useState<OptionItem[]>([])
	const [currentTab, setCurrentTab] = React.useState(resultTabs[0])
	let [filtersDialog, setFiltersDialog] = useState(false)
	let [showInsuranceCompanyModal, setShowInsuranceCompanyModal] = useState<boolean>(false)
	const [showLevelOfCoverModal, setShowLevelOfCoverModal] = useState<boolean>(false)
	let [activeResult, setActiveResult] = useState<ResultRow | null>(null)
	const [intervalStarted, setIntervalStarted] = useState<Date>(new Date)
	const [showError, setShowError] = useState<boolean>(false)
	const interval = useInterval(() => {
		fetchCompletedData(location?.state?.token ?? "")
	}, 5000)

	const notifyBackend = (quoteId: number, matrixId: number) => {
		if (location?.state?.token)
			doMatrixQuery(location?.state?.token, isProd, quoteId, matrixId)
	}

	const fetchCompletedData = async (token: string) => {
		try {
			const completedDataQueryResponse = await doQuotesQuery(token, isProd, referrer)
			const intervalElapsedTime = new Date().getTime() - intervalStarted.getTime()

			if (intervalElapsedTime > 120000 || completedDataQueryResponse.status === 401) {
				interval.stop()
				setIsLoading(false)
				setShowError(true)
			} else if (completedDataQueryResponse.status === 200) {
				interval.stop()

				const completedData = await completedDataQueryResponse.json()

				const fixedData = completedData.map((row: any) => {
					if (row.price && row.price.includes(",")) {
						row.price = parseFloat(row.price.replace(",", ""))
					}

					return row
				})

				initializeFilters(fixedData)
				setResults(fixedData)
				setOriginalResults(fixedData)
				setIsLoading(false)
			}

		} catch (error) {
			console.log("Error fetching completed data", error)
		}
	}

	const handleSidebarChange = () => {
		let filteredResults = originalResults

		if (levelOfCover !== "kaikki") {
			filteredResults = filteredResults.filter((row: ResultRow) => row.group.toLowerCase() === levelOfCover)
		}

		const optionalCommonNames: string[] = [];
		for (const optionalItem of optional) {
			const optionalCommonName = getCommonName(FilterType.OPTIONAL, optionalItem)
			if (optionalCommonNames.indexOf(optionalCommonName) === -1) optionalCommonNames.push(optionalCommonName)
		}

		if (optionalCommonNames.length > 0) {
			filteredResults = filteredResults.filter((result: ResultRow) => {
				// Check if every optionalCommonName is found in the result.coverages
				return optionalCommonNames.every(optionalCommonName => {
					return result.coverages.some((coverage: Coverage) => {
						const coverageCommonName = getCommonName(FilterType.OPTIONAL, coverage.name);
						return coverageCommonName === optionalCommonName;
					});
				})
			})
		}

		if (partners.length > 0) {
			filteredResults = filteredResults.filter((row: ResultRow) => partners.includes(row.insuranceCompany.name))
		}

		// TODO Filtered out stars rating for now in order to add EPSI rating to the results
		// if ( rating !== "" ) {
		// 	filteredResults = filteredResults.filter((row: ResultRow) => row.rating.stars >= parseInt(rating))
		// }

		setResults(filteredResults)
	}

	const handleOptionalChange = (optionalValue: string) => {
		const commonNameForValue = getCommonName(FilterType.OPTIONAL, optionalValue)

		const filteredOptions = filterOptions.filter((option: OptionItem) => option.commonName === commonNameForValue)

		let currentOptionals = [...optional]

		for (const option of filteredOptions) {
			if (currentOptionals.includes(option.name)) {
				currentOptionals = currentOptionals.filter(item => item !== option.name)
			} else {
				currentOptionals.push(option.name)
			}
		}

		setOptional(currentOptionals)
		handleCommonNameChange(commonNameForValue)
	}

	const handlePartnerChange = (partnerValue: string) => {
		let currentPartners = [...partners]

		if (currentPartners.includes(partnerValue)) {
			currentPartners = currentPartners.filter(item => item !== partnerValue)
		} else {
			currentPartners.push(partnerValue)
		}

		setPartners(currentPartners)
	}

	const handleCommonNameChange = (filterValue: string) => {
		let currentCommonNames = [...commonNames]
		if (currentCommonNames.includes(filterValue)) {
			currentCommonNames = currentCommonNames.filter(item => item !== filterValue)
		} else {
			currentCommonNames.push(filterValue)
		}

		setCommonNames(currentCommonNames)
	}

	const handleRemoveCommonName = (commonName: string) => {
		const options = filterOptions.filter(option => option.commonName === commonName)

		// remove all options from optional that have the commonName
		const currentOptionals = optional.filter(item => !options.some(option => option.name === item))

		setOptional(currentOptionals)

		const currentCommonNames = commonNames.filter(item => item !== commonName)

		setCommonNames(currentCommonNames)
	}

	const handleRemovePartner = (partner: string) => {
		const currentPartners = partners.filter(item => item !== partner)
		setPartners(currentPartners)
	}

	const initializeFilters = (queryResponse: any) => {

		const filterOptions: OptionItem[] = []

		queryResponse.forEach((resultRow: ResultRow) => {
			resultRow.coverages.forEach((item: Coverage) => {
				const commonName = getCommonName(FilterType.OPTIONAL, item.name)

				if (
					commonName &&
					!filterOptions.some((option: OptionItem) => option.name === item.name)
				) {
					const finalItem: { name: string, commonName: string } = {
						name: item.name,
						commonName: commonName
					}

					filterOptions.push(finalItem)
				}
			})
		})

		setFilterOptions(filterOptions)
	}

	const handleGoBack = () => {
		navigate({
			pathname: "/form/car",
			search: location.search,
		}, {
			state: {
				vehicleType: location?.state?.vehicleType,
				plateNumber: location.state.plateNumber,
				postalNumber: location.state.postalNumber,
			}
		})
	}

	useEffect(() => {
		handleSidebarChange()
	}, [levelOfCover, optional, rating, partners])

	useEffect(() => {
		if (!usePlaceholderResults) {
			interval.start();
			return interval.stop;
		} else {
			setResults(placeholderResults)
			setOriginalResults(placeholderResults)
			initializeFilters(placeholderResults)
			setIsLoading(false)
		}

		// Is there a need for this?
		// sendEvent(location?.state?.token, referrerIsEffortia, {
		// 	category: "",
		// 	action: "",
		// 	label: "",
		// 	queryId: queryId
		//  gclid: location?.state?.gclid ?? ""
		// })
	}, [location?.state?.token])

	useEffect(() => {
		if (!isLoading) {
			console.log("isEffortia: " + referrerIsEffortia)

			if (!(referrerIsEffortia || referrerIsVertaaensin)) setShowLevelOfCoverModal(true)
			if (referrerIsVertaaensin) setLevelOfCover("kaikki")

			// Set initial partners
			if (referrerIsEffortia || referrerIsVertaaensin) setPartners(["POP"])
		}
	}, [isLoading]);

	if (showError) {
		return (
			<div className="bg-white">
				<Overlay color="#000" backgroundOpacity={0.35} blur={15}>
					{!showVakuutustenvertailuLogo &&
						<div className="flex flex-row justify-center py-4 bg-[#2396D2]">
							<img
								src={logoSrc}
								alt={logoAlt}
								className="w-full max-w-[220px]"
							/>
						</div>
					}
					<div
						className="flex flex-row grow justify-center items-center w-screen h-full mx-auto bg-white overflow-hidden fixed">
						<div className="-mt-[80px]">
							{translations.results.errorLoadingData}
						</div>
					</div>
				</Overlay>
			</div>
		)
	} else return (
		<div className={ classNames(
			isVertaaensinTheme ? "vertaaensin-theme" : "",
			"bg-white"
		) }>
			{!showVakuutustenvertailuLogo &&
				<div className="flex flex-row justify-center py-4 bg-[#2396D2]">
					<img
						src={logoSrc}
						alt={logoAlt}
						className="w-full max-w-[220px]"
					/>
				</div>
			}
			{!isLoading &&
				<div className="flex flex-row justify-end px-4 block lg:hidden bg-[#F5F5F5]">
					<button
						onClick={() => setFiltersDialog(!filtersDialog)}
						className={classNames(
							primaryCtaClass,
							"my-2 ring-0! focus:ring-offset-0 outline:none duration-200 border py-3 px-8 font-semibold text-md rounded-md transition-all"
						)}>
						{translations.results.filtersButtonTitle}
					</button>
				</div>
			}
			<Dialog
				open={filtersDialog}
				onClose={() => setFiltersDialog(false)}
				className="relative z-50"
			>
				<div className="fixed inset-0 bg-black/30" />
				<div className="fixed inset-0 flex w-screen items-center justify-center">
					<Dialog.Panel className="w-full h-full overflow-y-scroll mx-auto rounded bg-white">
						<Dialog.Title
							className="fixed top-0 left-0 w-full border-b border-gray-300 bg-white z-50">
							<div className="flex flex-row justify-end px-4">
								<button
									onClick={() => setFiltersDialog(!filtersDialog)}
									className={classNames(
										primaryCtaClass,
										"my-2 ring-0! focus:ring-offset-0 outline:none duration-200 border py-3 px-8 font-semibold text-md rounded-md transition-all"
									)}>
									{translations.results.filtersDialogApplyButtonTitle}
								</button>
							</div>
						</Dialog.Title>
						<div className="fixed pt-[66px] w-full h-full overflow-y-scroll">
							<ResultFilters
								plateNumber={location?.state?.plateNumber}
								currentLevelOfCover={levelOfCover}
								currentOptional={optional}
								currentRating={rating}
								currentPartners={partners}
								results={results}
								allResults={originalResults}
								handleLevelOfCover={(value: string) => setLevelOfCover(value)}
								handleOptional={(value: string) => handleOptionalChange(value)}
								handleFilterByRating={(value: string) => setRating(value)}
								handlePartner={(value: string) => handlePartnerChange(value)}
								filterOptions={filterOptions}
								handleGoBack={handleGoBack}
							/>
						</div>
					</Dialog.Panel>
				</div>
			</Dialog>
			<Box pos="relative">
				<LoadingOverlay
					visible={isLoading}
					zIndex={1000}
					overlayProps={{ radius: "sm", blur: 2 }}
					loaderProps={{
						children: <div className="flex flex-row items-center space-x-4">
							<Loader color="blue" />
							<div>Pieni hetki, tuloksien hakeminen voi kestää jopa 30 sekuntia...</div>
						</div>
					}}
				/>
				<div className="flex flex-row flex-1 w-full max-w-screen-xl mx-auto bg-white">
					<div className="relative w-full hidden lg:block lg:w-1/4 py-10 space-y-2 bg-[#F5F5F5]">
						<ResultFilters
							plateNumber={location?.state?.plateNumber}
							currentLevelOfCover={levelOfCover}
							currentPartners={partners}
							currentOptional={optional}
							currentRating={rating}
							results={results}
							allResults={originalResults}
							handleLevelOfCover={(value: string) => setLevelOfCover(value)}
							handleOptional={(value: string) => handleOptionalChange(value)}
							handleFilterByRating={(value: string) => setRating(value)}
							handlePartner={(value: string) => handlePartnerChange(value)}
							filterOptions={filterOptions}
							handleGoBack={handleGoBack}
						/>
					</div>
					<div className="w-full lg:w-3/4 pt-12 pb-10 px-4 bg-[#FAFAFA]">
						<h2 className="text-2xl font-bold text-gray-700">{translations.results.mainTitle}</h2>
						<div className="mt-2 text-sm text-gray-500">
							Alla näytetään hakukriteereihin sopivat tulokset valitsemassasi
							järjestyksessä.<br /><br />
							<span className="font-medium">Asiakastyytyväisyys</span> perustuu puolueettomaan
							EPSi-tutkimukseen, jossa selvitetään
							asiakkaiden tyytyväisyyttä, uskollisuutta, yhtiön imagoa, tuotteiden ja palveluiden
							laatua sekä kokemusta vastineesta rahalle. EPSI:n asiakastyytyväisyys mitataan
							asteikolla 0-100.<br /><br />
							<span className="font-medium">Katso lisätietoja</span> -nappia painamalla näet
							vakuutuksen lisätiedot ja
							voit siirtyä ostamaan tämän vakuutuksen vakuutusyhtiön verkkokaupasta.
						</div>
						<SimpleGrid
							cols={{ base: 1, sm: 3, lg: 4 }}
							className="mt-0 py-4 min-h-[40px]"
						>
							{levelOfCover !== "kaikki" &&
								<Button
									variant="light"
									radius="xl"
									rightSection={
										<XMarkIcon className="w-4 h-4 text-blue-500" />
									}
									onClick={() => setLevelOfCover("kaikki")}
									className="min-w-[140px]"
								>
									{toCapitalFirstLetter(getVisibleValue(levelOfCover, lang))}
								</Button>
							}
							{partners.length > 0 && partners.map((partner) => (
								<Button
									variant="light"
									radius="xl"
									rightSection={
										<CloseButton
											component="a"
											variant="transparent"
											icon={<XMarkIcon className="w-4 h-4 text-blue-500" />}
											onClick={() => handleRemovePartner(partner)}
										/>
									}
									key={"PartnerButton" + partner}
								>
									{partner}
								</Button>
							))
							}

							{/*{ optional.length > 0 && optional.map((item) => (*/}
							{/*	<Button*/}
							{/*		variant="light"*/}
							{/*		radius="xl"*/}
							{/*		rightSection={*/}
							{/*			<CloseButton*/}
							{/*				variant="transparent"*/}
							{/*				icon={ <XMarkIcon className="w-4 h-4 text-blue-500"/> }*/}
							{/*				// onClick={ () => setOptional(optional.filter(currentItem => currentItem !== item)) }*/}
							{/*				onClick={ () => handleOptionalChange(item)}*/}
							{/*			/>*/}
							{/*		}*/}
							{/*		key={ item }*/}
							{/*	>*/}
							{/*		{ toCapitalFirstLetter(item) }*/}
							{/*	</Button>*/}
							{/*))*/}
							{/*}*/}

							{commonNames.length > 0 && commonNames.map((commonName) => (
								<Button
									variant="light"
									radius="xl"
									rightSection={
										<CloseButton
											component="a"
											variant="transparent"
											icon={<XMarkIcon className="w-4 h-4 text-blue-500" />}
											onClick={() => handleRemoveCommonName(commonName)}
										/>
									}
									key={commonName}
								>
									{toCapitalFirstLetter(getVisibleValue(commonName, lang))}
								</Button>

							))}
							{rating !== "" &&
								<Button
									variant="light"
									radius="xl"
									rightSection={
										<CloseButton
											component="a"
											variant="transparent"
											icon={<XMarkIcon className="w-4 h-4 text-blue-500" />}
										/>
									}
									onClick={() => setRating("")}
									key={rating}
								>
									{rating} stars
								</Button>
							}
						</SimpleGrid>
						<Tabs defaultValue="Best Price" className="w-full">
							<h4>{translations.results.tabsHeader}</h4>
							<div className="flex flex-row items-start justify-start mt-2 mb-6">
								<Tabs.List>
									{resultTabs.map((tab) => (
										<Tabs.Tab
											value={tab.name}
											key={tab.name}
											className={classNames(
												tab.name === currentTab.name
													? 'border-[#0F6CBD] text-gray-900'
													: 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
												'whitespace-nowrap border-b-2 py-4 px-1 text-sm font-medium cursor-pointer'
											)}
											onClick={() => setCurrentTab(tab)}
										>
											{tab.name === "Best Price" ? translations.results.tabBestPrice : translations.results.tabBestRating}
										</Tabs.Tab>
									))}
								</Tabs.List>
							</div>
							<div className="flex flex-col items-center justify-start my-6">
								<Tabs.Panel value="Best Price" className="w-full">
									<Accordion
										chevron={null}
										className="px-0"
									>
										{results && results.length > 0 &&
											results
												.sort((a: ResultRow, b: ResultRow) => a.priceValue - b.priceValue)
												.map((row: ResultRow, index: number) => (
													<Accordion.Item key={"BestPrice" + row.quoteId + index}
														value={"BestPrice" + row.quoteId + index}
														className="accordion-item mt-2 mb-4 border-none bg-white shadow">
														<Accordion.Control icon={null} className="px-0" component="div">
															<SingleResultRow
																place={index + 1}
																insuranceCompanyName={row.insuranceCompany.name}
																productName={row.productName}
																group={row.group}
																price={row.priceValue}
																rating={row.rating}
																coverages={row.coverages}
																key={"BestPrice" + row.quoteId + index}
																onShowInsuranceCompany={() => {
																	setActiveResult(row)
																}}
																token={location?.state?.token}
																gclid={location?.state?.gclid ?? ""}
															/>
														</Accordion.Control>
														<Accordion.Panel className="rounded-md bg-white">
															<Tabs defaultValue="tab1" variant="default" color="#0c589a">
																<Tabs.List grow className="font-bold">
																	<Tabs.Tab value="tab1">
																		<div className="text-lg">Yleiset tiedot</div>
																	</Tabs.Tab>
																	<Tabs.Tab value="tab2">
																		<div className="text-lg">Vakuutuksen tiedot</div>
																	</Tabs.Tab>
																	<Tabs.Tab
																		value="tab3"
																		disabled={ row.insuranceCompany?.name === "(Nimi piilotettu)" }
																	>
																		<div className="text-lg">Tietoa vakuutusyhtiöstä</div>
																	</Tabs.Tab>
																</Tabs.List>
																<Tabs.Panel value="tab1">
																	{activeResult?.insuranceCompany?.name !== "(Nimi piilotettu)" &&
																		<div id="insuranceCompanyModalHeader"
																			className={classNames(
																				"flex flex-row w-[90%] sm:w-auto mx-auto px-4",
																				insuranceHeaderBrandClass,
																				cleanString(activeResult?.insuranceCompany?.name.toLowerCase() ?? "")
																			)}></div>
																	}
																	<div className={classNames(
																		(activeResult?.insuranceCompany?.name !== "(Nimi piilotettu)") ? "mt-6" : "",
																		"flex flex-col w-full space-y-4 pt-6 border-t border-[#EAECF0]"
																	)}>
																		<div className="flex flex-col sm:flex-row mb-1">
																			<div
																				className="flex flex-col items-start justify-center basis-9/12 bg-[#F9FAFB] rounded-xl space-y-2 p-6">
																				<div>
																					{activeResult &&
																						<div
																							className="mb-2 font-bold text-[#101828]">{translations.singleResult.why} {activeResult?.insuranceCompany?.name}?</div>
																					}
																				</div>
																				{activeResult?.insuranceCompany?.name === "(Nimi piilotettu)" &&
																					<>{translations.singleResult.noAgreement}</>
																				}
																				{activeResult?.insuranceCompany?.name !== "(Nimi piilotettu)" &&
																					<div className="ml-4 text-normal">
																						<ol className="pl-4 list-decimal space-y-2">
																							{activeResult?.insuranceCompany?.name === "POP" &&
																								translations.results.insuranceCompanyValueProps.POP.map((item: string, index: number) => (
																									<li key={"valueProp" + index}>{item}</li>
																								))
																							}
																							{activeResult?.insuranceCompany?.name === "Fennia" &&
																								translations.results.insuranceCompanyValueProps.Fennia.map((item: string, index: number) => (
																									<li key={"valueProp" + index}>{item}</li>
																								))
																							}
																							{activeResult?.insuranceCompany?.name === "Turva" &&
																								translations.results.insuranceCompanyValueProps.Turva.map((item: string, index: number) => (
																									<li key={"valueProp" + index}>{item}</li>
																								))
																							}
																							{activeResult?.insuranceCompany?.name === "Lähitapiola" &&
																								translations.results.insuranceCompanyValueProps.Lähitapiola.map((item: string, index: number) => (
																									<li key={"valueProp" + index}>{item}</li>
																								))
																							}
																							{activeResult?.insuranceCompany?.name === "LähiTapiola" &&
																								translations.results.insuranceCompanyValueProps.LähiTapiola.map((item: string, index: number) => (
																									<li key={"valueProp" + index}>{item}</li>
																								))
																							}
																							{activeResult?.insuranceCompany?.name === "Pohjantähti" &&
																								translations.results.insuranceCompanyValueProps.Pohjantähti.map((item: string, index: number) => (
																									<li key={"valueProp" + index}>{item}</li>
																								))
																							}
																							{activeResult?.insuranceCompany?.name === "Pohjola Vakuutus" &&
																								<>
																									<li>OP:n omistaja-asiakkaana saat OP-bonuksia, joilla voi maksaa vakuutusmaksuja</li>
																									<li>Luotettava kotimainen ja vakavarainen yhtiö</li>
																								</>
																							}
																						</ol>
																					</div>
																				}
																			</div>
																			<div
																				className={classNames(
																					"h-[300px] basis-auto sm:basis-3/12 w-full insuranceCompanyModalBackgroundLady",
																					cleanString(activeResult?.insuranceCompany?.name.toLowerCase() ?? "")
																				)}
																			></div>
																		</div>
																		{activeResult?.insuranceCompany?.name === "POP" &&
																			<div className="flex flex-col px-6 pt-4 pb-6 space-y-6">
																				<div
																					className="font-bold text-2xl mb-0">{translations.results.nextStepTitle}
																				</div>
																				<div>{activeResult?.insuranceCompany?.name} {translations.results.nextStepDescription}
																				</div>
																				<div>{translations.results.smallestDeductible}</div>
																				{
																					(activeResult?.group === "Täyskasko" || activeResult?.group === "Osakasko") &&
																					<div>
																						Osa- ja täyskaskovakuutuksiin kuuluu myös
																						liikennevakuutus.
																					</div>
																				}
																				<div
																					className="font-bold">{translations.results.yourChoice}: {getProductName(activeResult?.productName ?? "", lang)}</div>
																				{optional && optional.length > 0 &&
																					<>
																						<div
																							className="font-bold">{translations.results.selectionInstructions}</div>
																						<ol className="ml-4 pl-4 list-decimal space-y-2">
																							{optional.map((item: string, index: number) => {
																								// if item is found in activeResult?.coverages, return the item, else skip
																								if (activeResult?.coverages.find(coverage => coverage.name === item)) {
																									return (
																										<li key={item + index}>{item}</li>
																									)
																								}
																							})}
																						</ol>
																					</>
																				}
																			</div>
																		}
																	</div>
																</Tabs.Panel>
																<Tabs.Panel value="tab2">
																	<div className="px-2 py-4">
																		<SimpleGrid
																			cols={{ base: 1, sm: 2 }}
																			spacing="lg"
																			verticalSpacing="lg"
																		>
																			<div>
																				<div className="flex flex-row align-middle">
																					<div
																						className="font-bold text-lg min-w-[120px] mr-2 mb-2">Vakuutus:
																					</div>
																					<div>{row.productName}</div>
																				</div>
																				<div className="flex flex-row align-middle">
																					<div className="font-bold min-w-[120px] mr-2">Omavastuu:
																					</div>
																					<div className="font-normal">200 €</div>
																				</div>
																				{/* <div
																					className="flex flex-row content-start align-middle space-2">
																					<div className="font-bold min-w-[120px]">Alennukset:</div>
																					<div className="font-normal pl-2">
																						<div>Kaskoalennus 25 %</div>
																						<div>Kaskobonus 83 %</div>
																						<div>Liikennebonus 70 %</div>
																					</div>
																				</div> */}
																			</div>
																			<div>
																				<div className="flex flex-row align-middle space-2">
																					<div
																						className="font-bold min-w-[120px] mr-2">Vakuutusturvat:
																					</div>
																					<div className="font-normal">
																						{row.coverages.map((coverage) => (
																							<div
																								key={"Coverage" + coverage.name}>{getVisibleValue(coverage.name, lang)}</div>
																						))}
																					</div>
																				</div>
																			</div>
																		</SimpleGrid>
																	</div>
																</Tabs.Panel>
																<Tabs.Panel value="tab3">
																	<div className="px-2 py-4">
																		<p>
																			{getCompanyDescription(row.insuranceCompany.name)}
																		</p>
																	</div>
																</Tabs.Panel>
															</Tabs>
															{companyHasDeal(activeResult?.insuranceCompany?.name) &&
																<div className={classNames(
																	(activeResult?.insuranceCompany?.name !== "(Nimi piilotettu)") ? "border-t border-[#EAECF0] mt-4" : "",
																	"flex flex-row items-center justify-end px-0 pt-4"
																)}>
																	{activeResult?.insuranceCompany?.name !== "(Nimi piilotettu)" &&
																		<button
																			className={classNames(
																				companyHasDeal(activeResult?.insuranceCompany?.name) ? primaryCtaClass : "bg-gray-500 hover:bg-gray-600 text-white",
																				"flex flex-row items-center my-2 duration-200 border py-4 px-10 font-semibold text-md rounded-md transition-all"
																			)}
																			onClick={() => {
																				if (!companyHasDeal(activeResult?.insuranceCompany?.name)) return

																				sendEvent(location?.state?.token, referrerIsEffortia, {
																					category: 'openLink',
																					action: 'click',
																					label: activeResult?.insuranceCompany?.url,
																					gclid: location?.state?.gclid ?? ""
																				})

																				if (activeResult && activeResult.quoteId && activeResult.matrixId) {
																					notifyBackend(activeResult.quoteId, activeResult.matrixId)
																				}

																				setTimeout(() => {
																					window.open(activeResult?.insuranceCompany?.url, "_blank")
																				}, 300)
																			}}
																			type="submit"
																		>
																			<ChevronRightIcon className="mr-2 h-4 w-4 text-gray-white" />
																			{translations.results.proceedToInsuranceCompanyButtonTitle}
																		</button>
																	}
																</div>
															}
														</Accordion.Panel>
													</Accordion.Item>
												))}
									</Accordion>
								</Tabs.Panel>
								<Tabs.Panel value="Best Rating" className="w-full">
									<Accordion
										chevron={null}
										className="px-0"
									>
										{results && results.length > 0 &&
											results
												.sort((a, b) => b.rating.insurerepsi - a.rating.insurerepsi)
												.map((row: ResultRow, index: number) => (
													<Accordion.Item key={"BestRating" + row.quoteId + index}
														value={"BestRating" + row.quoteId + index}
														className="accordion-item mt-2 mb-4 border-none bg-white shadow">
														<Accordion.Control icon={null} className="px-0" component="div">
															<SingleResultRow
																place={index + 1}
																insuranceCompanyName={row.insuranceCompany.name}
																productName={row.productName}
																group={row.group}
																price={row.priceValue}
																rating={row.rating}
																coverages={row.coverages}
																key={"BestRating" + row.quoteId + index}
																onShowInsuranceCompany={() => {
																	setActiveResult(row)
																}}
																token={location?.state?.token}
																gclid={location?.state?.gclid ?? ""}
															/>
														</Accordion.Control>
														<Accordion.Panel className="rounded-md bg-white">
															<Tabs defaultValue="tab1" variant="default" color="#0c589a">
																<Tabs.List grow className="font-bold">
																	<Tabs.Tab value="tab1">
																		<div className="text-lg">Yleiset tiedot</div>
																	</Tabs.Tab>
																	<Tabs.Tab value="tab2">
																		<div className="text-lg">Vakuutuksen tiedot</div>
																	</Tabs.Tab>
																	<Tabs.Tab
																		value="tab3"
																		disabled={ row.insuranceCompany?.name === "(Nimi piilotettu)" }
																	>
																		<div className="text-lg">Tietoa vakuutusyhtiöstä</div>
																	</Tabs.Tab>
																</Tabs.List>
																<Tabs.Panel value="tab1">
																	{activeResult?.insuranceCompany?.name !== "(Nimi piilotettu)" &&
																		<div id="insuranceCompanyModalHeader"
																			className={classNames(
																				"flex flex-row w-[90%] sm:w-auto mx-auto px-4",
																				insuranceHeaderBrandClass,
																				cleanString(activeResult?.insuranceCompany?.name.toLowerCase() ?? "")
																			)}></div>
																	}
																	<div className={classNames(
																		(activeResult?.insuranceCompany?.name !== "(Nimi piilotettu)") ? "mt-6" : "",
																		"flex flex-col w-full space-y-4 pt-6 border-t border-[#EAECF0]"
																	)}>
																		<div className="flex flex-col sm:flex-row mb-1">
																			<div
																				className="flex flex-col items-start justify-center basis-9/12 bg-[#F9FAFB] rounded-xl space-y-2 p-6">
																				<div>
																					{activeResult &&
																						<div
																							className="mb-2 font-bold text-[#101828]">{translations.singleResult.why} {activeResult?.insuranceCompany?.name}?</div>
																					}
																				</div>
																				{activeResult?.insuranceCompany?.name === "(Nimi piilotettu)" &&
																					<>{translations.singleResult.noAgreement}</>
																				}
																				{activeResult?.insuranceCompany?.name !== "(Nimi piilotettu)" &&
																					<div className="ml-4 text-normal">
																						<ol className="pl-4 list-decimal space-y-2">
																							{activeResult?.insuranceCompany?.name === "POP" &&
																								translations.results.insuranceCompanyValueProps.POP.map((item: string, index: number) => (
																									<li key={"valueProp" + index}>{item}</li>
																								))
																							}
																							{activeResult?.insuranceCompany?.name === "Fennia" &&
																								translations.results.insuranceCompanyValueProps.Fennia.map((item: string, index: number) => (
																									<li key={"valueProp" + index}>{item}</li>
																								))
																							}
																							{activeResult?.insuranceCompany?.name === "Turva" &&
																								translations.results.insuranceCompanyValueProps.Turva.map((item: string, index: number) => (
																									<li key={"valueProp" + index}>{item}</li>
																								))
																							}
																							{activeResult?.insuranceCompany?.name === "Lähitapiola" &&
																								translations.results.insuranceCompanyValueProps.Lähitapiola.map((item: string, index: number) => (
																									<li key={"valueProp" + index}>{item}</li>
																								))
																							}
																							{activeResult?.insuranceCompany?.name === "LähiTapiola" &&
																								translations.results.insuranceCompanyValueProps.LähiTapiola.map((item: string, index: number) => (
																									<li key={"valueProp" + index}>{item}</li>
																								))
																							}
																							{activeResult?.insuranceCompany?.name === "Pohjantähti" &&
																								translations.results.insuranceCompanyValueProps.Pohjantähti.map((item: string, index: number) => (
																									<li key={"valueProp" + index}>{item}</li>
																								))
																							}
																						</ol>
																					</div>
																				}
																			</div>
																			<div className={classNames(
																				"h-[300px] basis-auto sm:basis-3/12 w-full insuranceCompanyModalBackgroundLady",
																				cleanString(activeResult?.insuranceCompany?.name.toLowerCase() ?? "")
																			)}></div>
																		</div>
																		{activeResult?.insuranceCompany?.name === "POP" &&
																			<div className="flex flex-col px-6 pt-4 pb-6 space-y-6">
																				<div
																					className="font-bold text-2xl mb-0">{translations.results.nextStepTitle}
																				</div>
																				<div>{activeResult?.insuranceCompany?.name} {translations.results.nextStepDescription}
																				</div>
																				<div>{translations.results.smallestDeductible}</div>
																				{
																					(activeResult?.group === "Täyskasko" || activeResult?.group === "Osakasko") &&
																					<div>
																						Osa- ja täyskaskovakuutuksiin kuuluu myös
																						liikennevakuutus.
																					</div>
																				}
																				<div
																					className="font-bold">{translations.results.yourChoice}: {getProductName(activeResult?.productName ?? "", lang)}</div>
																				{optional && optional.length > 0 &&
																					<>
																						<div
																							className="font-bold">{translations.results.selectionInstructions}</div>
																						<ol className="ml-4 pl-4 list-decimal space-y-2">
																							{optional.map((item: string, index: number) => {
																								// if item is found in activeResult?.coverages, return the item, else skip
																								if (activeResult?.coverages.find(coverage => coverage.name === item)) {
																									return (
																										<li key={item + index}>{item}</li>
																									)
																								}
																							})}
																						</ol>
																					</>
																				}
																			</div>
																		}
																	</div>
																	{companyHasDeal(activeResult?.insuranceCompany?.name) &&
																		<div className={classNames(
																			(activeResult?.insuranceCompany?.name !== "(Nimi piilotettu)") ? "border-t border-[#EAECF0] mt-4" : "",
																			"flex flex-row items-center justify-end px-0 pt-4"
																		)}>
																			{activeResult?.insuranceCompany?.name !== "(Nimi piilotettu)" &&
																				<button
																					className={classNames(
																						companyHasDeal(activeResult?.insuranceCompany?.name) ? primaryCtaClass : "bg-gray-500 hover:bg-gray-600 text-white",
																						"flex flex-row items-center my-2 duration-200 border py-4 px-10 font-semibold text-md rounded-md transition-all"
																					)}
																					onClick={() => {
																						if (!companyHasDeal(activeResult?.insuranceCompany?.name)) return

																						sendEvent(location?.state?.token, referrerIsEffortia, {
																							category: 'openLink',
																							action: 'click',
																							label: activeResult?.insuranceCompany?.url,
																							gclid: location?.state?.gclid ?? ""
																						})

																						if (activeResult && activeResult.quoteId && activeResult.matrixId) {
																							notifyBackend(activeResult.quoteId, activeResult.matrixId)
																						}

																						setTimeout(() => {
																							window.open(activeResult?.insuranceCompany?.url, "_blank")
																						}, 300)
																					}}
																					type="submit"
																				>
																					<ChevronRightIcon
																						className="mr-2 h-4 w-4 text-gray-white" />
																					{translations.results.proceedToInsuranceCompanyButtonTitle}
																				</button>
																			}

																		</div>
																	}
																</Tabs.Panel>
																<Tabs.Panel value="tab2">
																	<div className="px-2 py-4">
																		<SimpleGrid
																			cols={{ base: 1, sm: 2 }}
																			spacing="lg"
																			verticalSpacing="lg"
																		>
																			<div>
																				<div className="flex flex-row align-middle">
																					<div
																						className="font-bold text-lg min-w-[120px] mr-2 mb-2">Vakuutus:
																					</div>
																					<div>{row.productName}</div>
																				</div>
																				<div className="flex flex-row align-middle">
																					<div className="font-bold min-w-[120px] mr-2">Omavastuu:
																					</div>
																					<div className="font-normal">200 €</div>
																				</div>
																				{/* <div
																					className="flex flex-row content-start align-middle space-2">
																					<div className="font-bold min-w-[120px]">Alennukset:</div>
																					<div className="font-normal pl-2">
																						<div>Kaskoalennus 25 %</div>
																						<div>Kaskobonus 83 %</div>
																						<div>Liikennebonus 70 %</div>
																					</div>
																				</div> */}
																			</div>
																			<div>
																				<div className="flex flex-row align-middle space-2">
																					<div
																						className="font-bold min-w-[120px] mr-2">Vakuutusturvat:
																					</div>
																					<div className="font-normal">
																						{row.coverages.map((coverage) => (
																							<div
																								key={"Coverage" + coverage.name}>{getVisibleValue(coverage.name, lang)}</div>
																						))}
																					</div>
																				</div>
																			</div>
																		</SimpleGrid>
																	</div>
																</Tabs.Panel>
																<Tabs.Panel value="tab3">
																	<div className="px-2 py-4">
																		<p>
																			{getCompanyDescription(row.insuranceCompany.name)}
																		</p>
																	</div>
																</Tabs.Panel>
															</Tabs>
														</Accordion.Panel>
													</Accordion.Item>
												))}
									</Accordion>
								</Tabs.Panel>
							</div>
						</Tabs>
						<Modal
							opened={showInsuranceCompanyModal}
							onClose={() => setShowInsuranceCompanyModal(false)}
							size="xl"
							withCloseButton={false}
						>
							{companyHasDeal(activeResult?.insuranceCompany?.name) &&
								<div className={classNames(
									(activeResult?.insuranceCompany?.name !== "(Nimi piilotettu)") ? "border-b border-[#EAECF0] mb-4" : "",
									"flex flex-row items-center justify-between px-4 pt-4"
								)}>
									<Button
										variant="light"
										color="gray"
										onClick={() => setShowInsuranceCompanyModal(false)}
										className="min-w-[140px] min-h-[59px] flex flex-row items-center my-2 duration-200 border py-4 px-10 text-white font-semibold text-md rounded-md transition-all"
									>
										{translations.results.closeModalTitle}
									</Button>
									{activeResult?.insuranceCompany?.name !== "(Nimi piilotettu)" &&
										<button
											className={classNames(
												companyHasDeal(activeResult?.insuranceCompany?.name) ? primaryCtaClass : "bg-gray-500 hover:bg-gray-600 text-white",
												"flex flex-row items-center my-2 duration-200 border py-4 px-10 font-semibold text-md rounded-md transition-all"
											)}
											onClick={() => {
												if (!companyHasDeal(activeResult?.insuranceCompany?.name)) return

												sendEvent(location?.state?.token, referrerIsEffortia, {
													category: 'openLink',
													action: 'click',
													label: activeResult?.insuranceCompany?.url,
													gclid: location?.state?.gclid ?? ""
												})

												if (activeResult && activeResult.quoteId && activeResult.matrixId) {
													notifyBackend(activeResult.quoteId, activeResult.matrixId)
												}

												setTimeout(() => {
													window.open(activeResult?.insuranceCompany?.url, "_blank")
												}, 300)
											}}
											type="submit"
										>
											<ChevronRightIcon className="mr-2 h-4 w-4 text-gray-white" />
											{translations.results.proceedToInsuranceCompanyButtonTitle}
										</button>
									}
								</div>
							}
							{activeResult?.insuranceCompany?.name !== "(Nimi piilotettu)" &&
								<div id="insuranceCompanyModalHeader"
									className={classNames(
										"flex flex-row w-[90%] sm:w-auto mx-auto px-4",
										insuranceHeaderBrandClass,
										cleanString(activeResult?.insuranceCompany?.name.toLowerCase() ?? "")
									)}></div>
							}
							<div className={classNames(
								(activeResult?.insuranceCompany?.name !== "(Nimi piilotettu)") ? "mt-6" : "",
								"flex flex-col w-full space-y-4 pt-6 border-t border-[#EAECF0]"
							)}>
								<div className="flex flex-col sm:flex-row mb-1">
									<div
										className="flex flex-col items-start justify-center basis-9/12 bg-[#F9FAFB] rounded-xl space-y-2 p-6">
										<div>
											{activeResult &&
												<div
													className="mb-2 font-bold text-[#101828]">{translations.singleResult.why} {activeResult?.insuranceCompany?.name}?</div>
											}
										</div>
										{activeResult?.insuranceCompany?.name === "(Nimi piilotettu)" &&
											<>{translations.singleResult.noAgreement}</>
										}
										{activeResult?.insuranceCompany?.name !== "(Nimi piilotettu)" &&
											<div className="ml-4 text-normal">
												<ol className="pl-4 list-decimal space-y-2">
													{activeResult?.insuranceCompany?.name === "POP" &&
														translations.results.insuranceCompanyValueProps.POP.map((item: string, index: number) => (
															<li key={"valueProp" + index}>{item}</li>
														))
													}
													{activeResult?.insuranceCompany?.name === "Fennia" &&
														translations.results.insuranceCompanyValueProps.Fennia.map((item: string, index: number) => (
															<li key={"valueProp" + index}>{item}</li>
														))
													}
													{activeResult?.insuranceCompany?.name === "Turva" &&
														translations.results.insuranceCompanyValueProps.Turva.map((item: string, index: number) => (
															<li key={"valueProp" + index}>{item}</li>
														))
													}
													{activeResult?.insuranceCompany?.name === "Lähitapiola" &&
														translations.results.insuranceCompanyValueProps.Lähitapiola.map((item: string, index: number) => (
															<li key={"valueProp" + index}>{item}</li>
														))
													}
													{activeResult?.insuranceCompany?.name === "LähiTapiola" &&
														translations.results.insuranceCompanyValueProps.LähiTapiola.map((item: string, index: number) => (
															<li key={"valueProp" + index}>{item}</li>
														))
													}
													{activeResult?.insuranceCompany?.name === "Pohjantähti" &&
														translations.results.insuranceCompanyValueProps.Pohjantähti.map((item: string, index: number) => (
															<li key={"valueProp" + index}>{item}</li>
														))
													}
												</ol>
											</div>
										}
									</div>
									<div
										className={classNames(
											"h-[300px] basis-auto sm:basis-3/12 w-full insuranceCompanyModalBackgroundLady",
											cleanString(activeResult?.insuranceCompany?.name.toLowerCase() ?? "")
										)}></div>
								</div>
								{activeResult?.insuranceCompany?.name === "POP" &&
									<div className="flex flex-col px-6 pt-4 pb-6 space-y-6">
										<div className="font-bold text-2xl mb-0">{translations.results.nextStepTitle}
										</div>
										<div>{activeResult?.insuranceCompany?.name} {translations.results.nextStepDescription}
										</div>
										<div>{translations.results.smallestDeductible}</div>
										{
											(activeResult?.group === "Täyskasko" || activeResult?.group === "Osakasko") &&
											<div>
												Osa- ja täyskaskovakuutuksiin kuuluu myös liikennevakuutus.
											</div>
										}
										<div
											className="font-bold">{translations.results.yourChoice}: {getProductName(activeResult?.productName ?? "", lang)}</div>
										{optional && optional.length > 0 &&
											<>
												<div
													className="font-bold">{translations.results.selectionInstructions}</div>
												<ol className="ml-4 pl-4 list-decimal space-y-2">
													{optional.map((item: string, index: number) => {
														// if item is found in activeResult?.coverages, return the item, else skip
														if (activeResult?.coverages.find(coverage => coverage.name === item)) {
															return (
																<li key={item + index}>{item}</li>
															)
														}
													})}
												</ol>
											</>
										}
									</div>
								}
							</div>
						</Modal>
						<Modal
							opened={showLevelOfCoverModal}
							onClose={() => setShowLevelOfCoverModal(false)}
							size="sm"
							withCloseButton={false}
						>
							<div
								className="flex flex-col items-start justify-center p-6 mx-4 rounded-md bg-white shadow">
								<fieldset className="w-full">
									<legend
										className="flex flex-row items-center mb-4 text-sm font-bold leading-6 text-gray-900">
										{translations.results.levelOfCoverTitle}
										<Popover className="relative ml-2">
											<Transition
											>
												<Popover.Panel className="absolute z-10 left-8 -top-10">
													<div
														className="flex flex-wrap w-64 p-4 border border-gray-300 rounded-md bg-white text-sm text-gray-900 font-normal shadow">
														<p
															style={{ whiteSpace: 'pre-line' }}>{translations.results.levelOfCoverMoreInformation}</p>
													</div>
												</Popover.Panel>
											</Transition>
										</Popover>
									</legend>
									<div className="flex flex-col space-y-2">
										<Group>
											{getLevelOptions(translations.results.levelOfCoverTitles)
												.sort((a, b) => a.order.localeCompare(b.order))
												.map((option) => (
													<div className="flex w-full items-center ml-3"
														key={"levelOption" + option.id}>
														<Radio
															onChange={(event: any) => {
																setLevelOfCover(event.target.id)
																setShowLevelOfCoverModal(false)
															}}

															id={option.id}
															name="notification-method"
															type="radio"
															className="w-5 h-5 border-gray-300 text-blue-600"
														/>
														<label htmlFor={option.id}
															className="ml-3 block text-sm font-normal leading-6 text-gray-900">
															{option.title} ({getResultCountForLevelOfCover(option.title, results)})
														</label>
													</div>
												))}
										</Group>
									</div>
								</fieldset>
							</div>
						</Modal>
					</div>
				</div>
			</Box>
		</div>
	)
}

export default Results
